/**
 * Pure input rules for the chat composer, kept out of the component so each one
 * can be pinned by a plain unit test (jsdom has no keyboard or pointer model).
 *
 * The behaviour mirrors the ChatGPT and Claude mobile apps: on a phone the
 * on-screen Return key adds a new line and the send button sends; on a
 * desktop Enter sends and Shift+Enter adds a line.
 */

/** True when the primary input is a finger (phones, most tablets). */
export function isCoarsePointer(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

type KeyLike = {
  key: string;
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
  isComposing?: boolean;
  /** Legacy, but Safari still reports 229 for the Enter that confirms an IME candidate. */
  keyCode?: number;
};

/** keyCode every browser uses for "an input method is handling this key". */
const IME_KEY_CODE = 229;

/**
 * Should this keydown send the message?
 *
 * - Enter during IME composition (Japanese, Chinese, Korean candidate
 *   selection) confirms the candidate and must never send. That covers Safari,
 *   which reports isComposing false but keyCode 229 on the confirming Enter.
 * - Shift+Enter is always a newline.
 * - Cmd/Ctrl+Enter always sends, on any device (a Bluetooth keyboard on an iPad).
 * - Plain Enter sends on a fine pointer (desktop), and is a newline on a phone.
 */
export function shouldSendOnKey(e: KeyLike, coarse: boolean): boolean {
  if (e.key !== 'Enter') return false;
  // Safari fires compositionend BEFORE the confirming keydown, so isComposing is
  // already false there; only keyCode 229 still marks it as the IME's Enter.
  if (e.isComposing || e.keyCode === IME_KEY_CODE) return false;
  if (e.shiftKey) return false;
  if (e.metaKey || e.ctrlKey) return true;
  return !coarse;
}

/** Files carried by a paste or drop (screenshots and copied images arrive as files). */
export function filesFromTransfer(
  transfer: Pick<DataTransfer, 'files'> | null | undefined,
): File[] {
  return transfer?.files ? Array.from(transfer.files) : [];
}

/** True if a drag carries files (as opposed to dragged text or a link). */
export function dragHasFiles(
  transfer: Pick<DataTransfer, 'types'> | null | undefined,
): boolean {
  return !!transfer && Array.from(transfer.types ?? []).includes('Files');
}
